import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import GaugeSelectorOption, { GaugeSelectorOptionPropType, SelectorOption } from './GaugeSelectorOption';

export interface GaugeSelectorProps<V, O = SelectorOption<V>> {
  value: V;
  options: O[];
  disabled?: boolean;
  onHover?: (value: V) => void;
  onChange?: (value: V) => void;
  onHoverEnd?: () => void;
}

const GaugeSelector = <V,>({ value, options, disabled, onHover, onChange, onHoverEnd }: GaugeSelectorProps<V>) => {
  const [hovered, setHovered] = useState(-1);

  const handleHover = useCallback(
    (value) => {
      setHovered(value);
      if (typeof onHover === 'function') {
        onHover(value);
      }
    },
    [onHover],
  );

  const handleHoverEnd = useCallback(() => {
    setHovered(-1);
    if (typeof onHoverEnd === 'function') {
      onHoverEnd();
    }
  }, [onHoverEnd]);

  if (!options) {
    return null;
  }

  const className = classnames({
    'gauge-selector': true,
    'gauge-selector_disabled': disabled,
  });

  return (
    <div className={className}>
      {options.map((option) => (
        <GaugeSelectorOption
          option={option}
          disabled={disabled}
          // @ts-expect-error allow use value as a key
          key={option.value}
          isCurrent={option.value === value}
          isActive={option.value <= value}
          isHovered={option.value <= hovered}
          onHover={handleHover}
          onChange={onChange}
          onHoverEnd={handleHoverEnd}
        />
      ))}
    </div>
  );
};

GaugeSelector.propTypes = {
  value: PropTypes.number,
  options: PropTypes.arrayOf(GaugeSelectorOptionPropType || PropTypes.object),
  onHover: PropTypes.func,
  onHoverEnd: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
};

// eslint-disable-next-line import/no-default-export
export default React.memo(GaugeSelector);
